/**
 * React hook for managing KPI-related subscription data.
 * Subscribes to real-time KPI Variant and Root KPI update/delete events using AppSync.
 * Invalidates relevant React Query caches when KPI Variants or Root KPIs are modified externally.
 *
 * NOTE: KPISubscriptionContext is the primary subscription owner for most consumers.
 * This hook remains available for standalone cache invalidation use cases.
 */

import {
  onDeleteKPIVariant,
  onUpdateKPIVariant,
  onUpdateRootKPI,
} from '@services/api/deckManagement/graphql/schema/subscriptions';

import { kpiVariantQueryKeys } from './useKPIVariants';
import { rootKPIQueryKeys } from './useRootKPIs';
import { createSubscriptionHook, SubscriptionHookName } from './useSubscriptionFactory';

/**
 * Interface for KPI Subscription parameters
 * @param enabled - Whether the subscription should be made active
 * @interface
 */
export interface UseKPISubscriptionParams {
  enabled?: boolean;
}

/**
 * Creates a subscription hook for KPI events (variant update, variant delete, root KPI update).
 */
const kpiSubscriptionHook = createSubscriptionHook(SubscriptionHookName.KPISubscription, [
  {
    query: onUpdateKPIVariant,
    eventType: 'onUpdateKPIVariant',
    getInvalidateKeys: () => [kpiVariantQueryKeys.all],
  },
  {
    query: onDeleteKPIVariant,
    eventType: 'onDeleteKPIVariant',
    getInvalidateKeys: () => [kpiVariantQueryKeys.all, rootKPIQueryKeys.all],
  },
  {
    query: onUpdateRootKPI,
    eventType: 'onUpdateRootKPI',
    getInvalidateKeys: () => [rootKPIQueryKeys.all, kpiVariantQueryKeys.all],
  },
]);

/**
 * Hook for subscribing to KPI-related events to support real-time multi-user updates.
 *
 * Subscriptions:
 * - onUpdateKPIVariant: Invalidates all variant queries
 * - onDeleteKPIVariant: Invalidates all variant and root KPI queries (root's variant list is stale)
 * - onUpdateRootKPI: Invalidates all root KPI and variant queries
 *
 * @param enabled - Whether the subscription should be made active
 */
export const useKPISubscription = ({ enabled = false }: UseKPISubscriptionParams): void => {
  kpiSubscriptionHook({ enabled });
};
